import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import YearSelect from "./YearDropdown";

// Custom bar shape with rounded top corners only
const RoundedBar = (props) => {
  const { fill, x, y, width, height } = props;
  const radius = 6;

  if (height <= radius) {
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        rx={Math.min(radius, height / 2)}
        ry={Math.min(radius, height / 2)}
      />
    );
  }

  return (
    <path
      d={`M ${x + radius},${y} 
         L ${x + width - radius},${y} 
         Q ${x + width},${y} ${x + width},${y + radius}
         L ${x + width},${y + height} 
         L ${x},${y + height} 
         L ${x},${y + radius} 
         Q ${x},${y} ${x + radius},${y} Z`}
      fill={fill}
    />
  );
};

const BarAnalyticsChart = ({
  data,
  showToggle = true,
  showFilters = true,
  showYearDropdown = true,
  heading = "",
  onYearChange,
  selectedYear,
  isLoading = false,
  onFilterChange,
}) => {
  const [chartType, setChartType] = useState("bar"); // "bar" | "live"
  const [liveData, setLiveData] = useState([]);

  // INITIAL LIVE DATA - use actual data values
  useEffect(() => {
    setLiveData(data.map((d) => ({ ...d })));
  }, [data]);

  // LIVE UPDATE EVERY 2s (optional for dynamic data)
  // useEffect(() => {
  //   if (chartType !== "live") return;

  //   const interval = setInterval(() => {
  //     setLiveData((prev) =>
  //       prev.map((d) => ({
  //         ...d,
  //         value: d.value + (Math.random() * 5000 - 2500), // Smooth change
  //       }))
  //     );
  //   }, 2000);

  //   return () => clearInterval(interval);
  // }, [chartType]);

  // Color logic for bars
  const dataWithColors = data.map((item, index) => ({
    ...item,
    color: "#1FADC6",
  }));

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-4 sm:p-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
          {heading}
        </h2>

        {/* YEAR DROPDOWN (ONLY when toggle is OFF) */}
        {!showToggle && showYearDropdown && (
          <div className="flex items-center gap-2 flex-wrap">
            <YearSelect onChange={onYearChange} value={selectedYear} />
          </div>
        )}
      </div>

      {/* FILTERS + YEAR + TOGGLE (ONLY when toggle is ON) */}
      {showToggle && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-2 mb-4">
          {/* LEFT — CHART TOGGLE BUTTONS */}
          <div className="flex gap-2 sm:gap-3 flex-wrap">
            <button
              onClick={() => setChartType("bar")}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm border shadow-sm whitespace-nowrap ${
                chartType === "bar"
                  ? "bg-[#1FADC6] text-white border-[#1FADC6]"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              Bar Chart
            </button>

            <button
              onClick={() => setChartType("live")}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm border shadow-sm whitespace-nowrap ${
                chartType === "live"
                  ? "bg-[#1FADC6] text-white border-[#1FADC6]"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              Line Chart
            </button>
          </div>

          {/* RIGHT — YEAR + FILTER BUTTONS */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* YEAR AS FILTER BUTTON */}
            {showYearDropdown && (
              <div className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg">
                <YearSelect
                  compact
                  onChange={onYearChange}
                  value={selectedYear}
                />
              </div>
            )}

            {/* FILTERS */}
            {showFilters && (
              <>
                <button
                  onClick={() => onFilterChange("Last 3 Months")}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border bg-gray-100 hover:bg-gray-200 whitespace-nowrap"
                >
                  Last 3 Months
                </button>

                <button
                  onClick={() => onFilterChange("Last 6 Months")}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border bg-gray-100 hover:bg-gray-200 whitespace-nowrap"
                >
                  Last 6 Months
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* CHART */}
      <div
        className="w-full overflow-hidden"
        style={{ minHeight: "250px", height: "300px" }}
      >
        {isLoading ? (
          // Skeleton for chart
          <div className="animate-pulse h-[300px] flex items-end justify-between gap-2 px-8 pb-8 pt-4">
            {/* Y-axis skeleton */}
            <div className="flex flex-col justify-between h-full py-4 mr-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-3 w-8 bg-gray-200 rounded"></div>
              ))}
            </div>
            {/* Bars skeleton */}
            <div className="flex-1 flex items-end justify-between gap-3">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center gap-2 flex-1"
                >
                  <div
                    className="w-full bg-gray-200 rounded-t-md"
                    style={{
                      height: `${Math.random() * 150 + 50}px`,
                    }}
                  ></div>
                  <div className="h-3 w-8 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "bar" ? (
              <BarChart
                data={dataWithColors}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  axisLine={{ stroke: "#E5E7EB" }}
                />
                <YAxis
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  axisLine={{ stroke: "#E5E7EB" }}
                  domain={[
                    0,
                    Math.max(...dataWithColors.map((item) => item.value)),
                  ]} // Dynamic maximum value
                  ticks={[
                    0,
                    Math.max(...dataWithColors.map((item) => item.value)) / 4,
                    Math.max(...dataWithColors.map((item) => item.value)) / 2,
                    Math.max(...dataWithColors.map((item) => item.value)) *
                      0.75,
                    Math.max(...dataWithColors.map((item) => item.value)),
                  ]} // Dynamic ticks
                  tickFormatter={(v) => `${v / 1000}k`} // Format values as 'k'
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="value" shape={<RoundedBar />} activeBar={false}>
                  {dataWithColors.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <AreaChart
                data={liveData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="liveColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1FADC6" stopOpacity={0.4} />
                    <stop
                      offset="100%"
                      stopColor="#1FADC6"
                      stopOpacity={0.05}
                    />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="month"
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                />
                <YAxis
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  domain={[
                    0,
                    liveData.reduce(
                      (max, item) => Math.max(max, item.value),
                      0
                    ),
                  ]}
                  tickFormatter={(v) => `${v / 1000}k`}
                />

                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                  }}
                />

                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#1FADC6"
                  fill="url(#liveColor)"
                  strokeWidth={3}
                  isAnimationActive={false}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BarAnalyticsChart;
